import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSnackBar } from 'apps/auth/actions/appAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import RequestDetails from 'apps/auth/components/history/request/RequestDetails';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { COPY_RRN_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    EMAIL_POPUP,
    RENAME_POPUP,
    REQUEST_DETAILS_MODAL,
    REQUEST_MORE_POPUP,
    RRN_POPUP,
    SEND_CHEQUE_MODAL,
    SEND_MORE_POPUP,
    TRANSFER_STATUS_SCREEN_MODAL,
} from 'apps/auth/constants/ComponentNames';
import { AUTH_REQUEST_MAIN_LINK, AUTH_SEND_MAIN_LINK, HELP_MOBILE } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { getTableTextColor } from 'apps/auth/helpers/historyUtils';
import { copyTextToClipboard, getDDMonEEEHHmmDate, getHistoryTransactionFullDate, getLangCountryPrefix } from 'apps/auth/helpers/utils';
import { useApp, useCheck, useChequeExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import RequestMorePopup from './request/popups/RequestMorePopup';
import SendCheque from './send/cheque/SendCheque';
import EmailPopup from './send/popup/EmailPopup';
import RenamePopup from './send/popup/RenamePopup';
import SendMorePopup from './send/popup/SendMorePopup';
import TransferStatusScreen from './send/TransferStatusScreen/TransferStatusScreen';

const DetailsModal = (props) => {
    const { noTransactionDetailsTitle, goBack, helpAndMore } = usePageMessages();
    const { lang } = useLanguage();
    const { country } = useApp();
    const { paymentStatusHistory } = useChequeExtra();
    const cheque = useCheck();
    const messages = usePageMessages();

    const [popup, setPopup] = useState('');
    const [modal, setModal] = useState(props.isSend ? SEND_CHEQUE_MODAL : REQUEST_DETAILS_MODAL);
    const popupRef = useRef(null);

    const getPopupComponent = () => {
        if (popup === SEND_MORE_POPUP) {
            return (
                <SendMorePopup
                    popupRef={popupRef}
                    showEmailPopup={showEmailPopup}
                    showRenamePopup={showRenamePopup}
                    showSupportChat={cheque.chatAvailable}
                    faqUrl={`${HELP_MOBILE}${cheque.faqUrl || ''}`}
                    showRrnPopup={showRrnPopup}
                />
            );
        }
        if (popup === REQUEST_MORE_POPUP) {
            return <RequestMorePopup popupRef={popupRef} showSupportChat={false} faqUrl={`${HELP_MOBILE}${cheque.faqUrl || ''}`} />;
        }
        if (popup === EMAIL_POPUP) {
            return <EmailPopup popupRef={popupRef} />;
        }
        if (popup === RENAME_POPUP) {
            return <RenamePopup popupRef={popupRef} />;
        }

        if (popup === RRN_POPUP) {
            return (
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={messages.rrnTitle} />} popupRef={popupRef}>
                    <PopupButton
                        title={messages.copyRRNButton}
                        iconName="copy"
                        text={messages.copyRRNButton}
                        onClick={(e) => {
                            amplitude.getInstance().logEvent(COPY_RRN_CLICKED);
                            copyOnClick(cheque?.extra?.fromRRN, e);
                        }}
                    />
                    <PopupButton
                        text={messages.morePopupGetHelpButton}
                        iconName="lifebuoy"
                        href={`${getLangCountryPrefix(lang, country)}${HELP_MOBILE}${cheque.rrnFaqLink || cheque.faqUrl}`}
                    />
                </SimplePopup>
            );
        }
    };

    const showEmailPopup = () => {
        setPopup(EMAIL_POPUP);
        popupRef.current?.open();
    };

    const showRenamePopup = () => {
        setPopup(RENAME_POPUP);
        popupRef.current?.open();
    };

    const showRrnPopup = () => {
        setPopup(RRN_POPUP);
        popupRef.current?.open();
    };

    const moreClickHandler = (e) => {
        e.preventDefault();
        setPopup(props.isSend ? SEND_MORE_POPUP : REQUEST_MORE_POPUP);
        popupRef.current?.open();
    };

    const onCloseModalClick = (e) => {
        e.preventDefault();
        setModal(SEND_CHEQUE_MODAL);
    };

    const copyOnClick = (value, e) => {
        e.preventDefault();
        const result = copyTextToClipboard(value);
        if (result) {
            props.toggleSnackBar(true, messages.rrnNumberCopied);
        }
    };

    let modalProps;
    if (modal === TRANSFER_STATUS_SCREEN_MODAL) {
        modalProps = paymentStatusHistory?.length > 0 ? { onCloseIconClick: onCloseModalClick } : { canCloseModal: false };
    } else {
        modalProps = {
            linkTo: props.isSend ? AUTH_SEND_MAIN_LINK : AUTH_REQUEST_MAIN_LINK,
            onClickMoreIcon: moreClickHandler,
            isButtonWithIcon: true,
        };
    }

    const getFormattedChequeDate = (userCountry, chequeDate, lang) => {
        if (userCountry === USA) {
            const date = getHistoryTransactionFullDate(chequeDate, lang, userCountry);
            return (
                <>
                    {`${date.formattedDate} `}
                    <Text color={getTableTextColor(cheque)} nowrap>
                        {date.formattedTime}
                    </Text>
                </>
            );
        } else {
            return getDDMonEEEHHmmDate(chequeDate, lang);
        }
    };

    return (
        <>
            <ModalComponent {...modalProps} helpAndMore={helpAndMore} isButtonWithIcon={modal === SEND_CHEQUE_MODAL || modal === REQUEST_DETAILS_MODAL}>
                {modal === SEND_CHEQUE_MODAL && <SendCheque setModal={setModal} getFormattedChequeDate={getFormattedChequeDate} />}
                {modal === REQUEST_DETAILS_MODAL && <RequestDetails getFormattedChequeDate={getFormattedChequeDate} />}
                {modal === TRANSFER_STATUS_SCREEN_MODAL && (
                    <TransferStatusScreen
                        paymentStatusHistory={paymentStatusHistory}
                        lang={lang}
                        goBack={goBack}
                        noTransactionDetailsTitle={noTransactionDetailsTitle}
                        onCloseModalClick={onCloseModalClick}
                        cheque={cheque}
                    />
                )}
            </ModalComponent>
            <PopupNew ref={popupRef} getPopupComponent={getPopupComponent} />
        </>
    );
};

export default connect(null, { toggleSnackBar })(DetailsModal);
